(() => {
  if (customElements.get('recently-viewed-products')) return;

  const STORAGE_KEY = 'sylavie:recently-viewed:v1';
  const MAX_HISTORY = 12;
  const MAX_AGE = 30 * 24 * 60 * 60 * 1000;
  const CONCURRENCY = 2;
  let privacyReady;
  let recordedHandle;

  function normalizeHistory(value, now) {
    if (!Array.isArray(value)) return [];
    const seen = new Set();
    return value
      .filter((entry) => entry && typeof entry.handle === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(entry.handle)
        && Number.isFinite(entry.viewedAt) && entry.viewedAt <= now && now - entry.viewedAt < MAX_AGE)
      .sort((a, b) => b.viewedAt - a.viewedAt)
      .filter((entry) => !seen.has(entry.handle) && seen.add(entry.handle))
      .slice(0, MAX_HISTORY)
      .map(({ handle, viewedAt }) => ({ handle, viewedAt }));
  }

  function loadPrivacy() {
    if (window.Shopify?.customerPrivacy?.preferencesProcessingAllowed) return Promise.resolve();
    if (!privacyReady) {
      privacyReady = new Promise((resolve) => {
        let attempts = 0;
        const load = () => {
          if (window.Shopify?.loadFeatures) {
            try {
              // Match the version already loaded by this storefront's Shopify banner.
              window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], resolve);
            } catch (_) {
              resolve();
            }
          } else if (++attempts < 20) {
            window.setTimeout(load, 250);
          } else {
            resolve();
          }
        };
        load();
      });
    }
    return privacyReady;
  }

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.mount = this.querySelector('[data-recently-viewed-mount]');
      this.section = this.closest('.shopify-section');
      if (!this.mount || !this.section) return;

      // Keep an observable, zero-height anchor outside the fully hidden section.
      this.anchor = document.createElement('div');
      this.anchor.setAttribute('aria-hidden', 'true');
      // Dawn hides empty divs. The zero-height anchor must still have a box
      // outside the hidden section for IntersectionObserver to observe it.
      this.anchor.setAttribute('style', 'display: block;');
      this.placeAnchor();
      this.onConsent = (event) => this.refresh({ withdrawn: event.detail?.preferencesAllowed === false });
      this.onPageHide = () => { recordedHandle = undefined; this.reset(); };
      this.onPageShow = (event) => { if (event.persisted) this.refresh(); };
      this.onSectionLoad = () => this.placeAnchor();
      this.onStorage = (event) => {
        if (event.key !== STORAGE_KEY && event.key !== null) return;
        if (event.newValue === null) { recordedHandle = undefined; this.reset(); }
        else this.refresh({ record: false });
      };
      document.addEventListener('visitorConsentCollected', this.onConsent);
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('storage', this.onStorage);
      this.refresh();
    }

    disconnectedCallback() {
      this.connected = false;
      this.reset();
      this.anchor?.remove();
      document.removeEventListener('visitorConsentCollected', this.onConsent);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('storage', this.onStorage);
    }

    placeAnchor() {
      // Do not insert a sibling between sections: Dawn's .section + .section
      // spacing must still apply when Recently Viewed becomes visible.
      if (this.section.previousElementSibling) this.section.previousElementSibling.append(this.anchor);
      else this.section.before(this.anchor);
    }

    allowed() {
      try {
        return window.Shopify?.customerPrivacy?.preferencesProcessingAllowed() === true;
      } catch (_) {
        return false;
      }
    }

    reset() {
      this.generation = (this.generation || 0) + 1;
      this.observer?.disconnect();
      this.controller?.abort();
      this.hidden = true;
      this.mount?.replaceChildren();
    }

    async refresh({ withdrawn = false, record = true } = {}) {
      this.reset();
      const generation = this.generation;
      await loadPrivacy();
      if (!this.connected || generation !== this.generation) return;
      // An unavailable API is not a consent decision. Stay hidden without
      // touching history until permissions can actually be checked.
      if (!withdrawn && typeof window.Shopify?.customerPrivacy?.preferencesProcessingAllowed !== 'function') return;
      if (withdrawn || !this.allowed()) {
        // Removal is allowed on withdrawal; never read stored history without permission.
        try { window.localStorage.removeItem(STORAGE_KEY); } catch (_) { /* Storage may be blocked. */ }
        recordedHandle = undefined;
        return;
      }
      if (this.dataset.pageType !== 'product' || !this.dataset.productId || !this.dataset.productHandle) return;

      let history;
      try {
        let stored;
        try { stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]'); } catch (error) {
          if (error instanceof SyntaxError) stored = [];
          else throw error;
        }
        const now = Date.now();
        history = normalizeHistory(stored, now);
        if (record && !window.Shopify?.designMode && recordedHandle !== this.dataset.productHandle) {
          history = [{ handle: this.dataset.productHandle, viewedAt: now },
            ...history.filter((entry) => entry.handle !== this.dataset.productHandle)].slice(0, MAX_HISTORY);
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
          recordedHandle = this.dataset.productHandle;
        }
      } catch (_) {
        return;
      }

      const handles = history.filter((entry) => entry.handle !== this.dataset.productHandle).map((entry) => entry.handle);
      if (!handles.length) return;
      let started = false;
      const start = () => {
        if (started) return;
        started = true;
        this.observer?.disconnect();
        if (generation === this.generation && this.connected && this.allowed()) void this.loadCards(handles, generation);
      };
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) start();
        }, { rootMargin: '0px 0px 400px 0px' });
        this.observer.observe(this.anchor);
      } else {
        // Defer even when the browser lacks IntersectionObserver.
        window.setTimeout(start, 0);
      }
    }

    fragmentURL(handle) {
      const root = window.Shopify?.routes?.root || this.dataset.rootUrl;
      const url = new URL(`${root.replace(/\/?$/, '/')}products/${encodeURIComponent(handle)}`, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      // Existing preview cookies / the CLI proxy select the theme. Do not try to
      // select it with preview_theme_id; validate the returned theme ID instead.
      return url;
    }

    async fetchCard(handle, signal) {
      const request = new AbortController();
      const timeout = window.setTimeout(() => request.abort(), 8000);
      const abort = () => request.abort();
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) request.abort();
      try {
        const response = await fetch(this.fragmentURL(handle), { signal: request.signal, credentials: 'same-origin' });
        if (!response.ok || response.redirected) return null;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fragment = html.querySelector('recently-viewed-products');
        if (!fragment || fragment.dataset.pageType !== 'product' || !fragment.dataset.productId
          || fragment.dataset.productHandle !== handle) return null;
        for (const key of ['themeId', 'country', 'currency', 'language', 'sectionId']) {
          if (!this.dataset[key] || fragment.dataset[key] !== this.dataset[key]) return null;
        }
        const card = fragment.querySelector('[data-recently-viewed-card]')?.content.firstElementChild;
        return card?.matches('li.slider__slide') && card.querySelector('a[href]') ? card.cloneNode(true) : null;
      } catch (_) {
        return null;
      } finally {
        window.clearTimeout(timeout);
        signal.removeEventListener('abort', abort);
      }
    }

    async loadCards(handles, generation) {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const limit = Math.min(6, Math.max(1, Number(this.dataset.limit) || 6));
      const cards = [];
      // Ordered batches preserve recency, cap concurrency, and fetch older
      // candidates only when a newer card was missing or could not be resolved.
      for (let offset = 0; offset < handles.length && cards.length < limit; ) {
        if (signal.aborted || generation !== this.generation || !this.allowed()) return;
        const batch = handles.slice(offset, offset + Math.min(CONCURRENCY, limit - cards.length));
        offset += batch.length;
        const results = await Promise.all(batch.map((handle) => this.fetchCard(handle, signal)));
        cards.push(...results.filter(Boolean));
      }
      if (signal.aborted || generation !== this.generation || !this.connected || !this.allowed() || !cards.length) return;
      const carousel = this.querySelector('[data-recently-viewed-carousel]').content.cloneNode(true);
      carousel.querySelector('[data-related-products-track]').append(...cards);
      this.hidden = false;
      this.mount.replaceChildren(carousel);
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
})();
